import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  ExternalLink, Star, MessageSquare, Image, Code, 
  Search, FileText, Briefcase, Sparkles
} from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  "Assistant Général": <MessageSquare className="w-5 h-5" />,
  "Génération d'Images": <Image className="w-5 h-5" />,
  "Création d'Applications": <Code className="w-5 h-5" />,
  "Recherche": <Search className="w-5 h-5" />,
  "Création de Contenu": <FileText className="w-5 h-5" />,
  "Productivité Bureau": <Briefcase className="w-5 h-5" />
};

const OutilsIAPage = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchTools = async () => {
      try {
        const response = await axios.get(`${API}/ai-tools`);
        setTools(response.data);
      } catch (error) {
        console.error("Error fetching tools:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTools();
  }, []);

  const categories = ["all", ...new Set(tools.map(t => t.category))];
  
  const filteredTools = selectedCategory === "all" 
    ? tools 
    : tools.filter(t => t.category === selectedCategory);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="outils-ia-page">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="hero-glow" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              Outils <span className="text-[#007AFF]">IA</span> Populaires
            </h1>
            <p className="text-xl text-white/60 leading-relaxed">
              Découvrez les meilleurs outils d'Intelligence Artificielle du moment. 
              De ChatGPT à Emergent, trouvez l'outil adapté à vos besoins.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-[#007AFF] text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`filter-${category}`}
              >
                {category === "all" ? "Tous" : category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="py-8 pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6">
            {loading ? (
              Array(8).fill(0).map((_, i) => (
                <div key={i} className="glass-card p-6 loading-shimmer h-48" />
              ))
            ) : (
              filteredTools.map((tool, index) => (
                <motion.div
                  key={tool.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className={`tool-card glass-card p-6 glass-card-hover ${
                    tool.featured ? 'featured' : ''
                  }`}
                  data-testid={`tool-card-${tool.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        tool.featured ? 'bg-[#FFD700]/20' : 'bg-[#007AFF]/10'
                      }`}>
                        {categoryIcons[tool.category] || <Sparkles className="w-5 h-5" />}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          {tool.name}
                          {tool.highlight && (
                            <Star className="w-4 h-4 text-[#FFD700] fill-[#FFD700]" />
                          )}
                        </h3>
                        <p className="text-white/50 text-sm">{tool.provider}</p>
                      </div>
                    </div>
                  </div>

                  <p className="text-white/70 leading-relaxed mb-4">
                    {tool.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs text-white/40">Catégorie</span>
                      <p className="text-sm text-[#007AFF]">{tool.category}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-white/40">Tarif</span>
                      <p className="text-sm text-white/70">{tool.pricing}</p>
                    </div>
                  </div>

                  <a
                    href={tool.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 flex items-center justify-center gap-2 w-full py-3 rounded-full bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                    data-testid={`tool-link-${tool.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    Découvrir
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Emergent Highlight */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-8 md:p-12 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#007AFF]/10 to-[#FFD700]/10" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFD700]/20 text-[#FFD700] text-sm mb-4">
                <Sparkles className="w-4 h-4" />
                Entièrement réalisé par l'Intelligence Artificielle
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ce site a été créé avec Emergent
              </h2>
              <p className="text-white/60 text-lg max-w-2xl mb-8">
                Tout ce que vous voyez ici - le design, le code, les fonctionnalités - 
                a été entièrement réalisé par l'Intelligence Artificielle avec Emergent 
                en quelques minutes. Imaginez ce que vous pourriez créer...
              </p>
              
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://emergent.sh"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="emergent-cta"
                >
                  Essayer Emergent
                  <ExternalLink className="w-4 h-4" />
                </a>
                <Link
                  to="/jeu"
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="game-cta"
                >
                  Voir la démo interactive
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OutilsIAPage;
